import type { Answer, HistoryEntry, Route, Scene, Story } from "./types";

export function routeMatches(route: Route, answers: Answer[]): boolean {
  return answers.every((answer, index) => route.choices[index] === answer);
}

export function getSceneId(story: Story, history: HistoryEntry[]): string {
  const answers = history.map(({ answer }) => answer);
  const candidates = story.routes.filter((route) => routeMatches(route, answers));
  const sceneIds = new Set(candidates.map((route) => route.sceneIds[history.length]));
  if (sceneIds.size !== 1) {
    throw new Error(`No unambiguous scene for choice prefix ${answers.join("/")}`);
  }
  return [...sceneIds][0];
}

export function getCompletedRoute(story: Story, history: HistoryEntry[]): Route {
  if (history.length !== 6) {
    throw new Error("A route completes after exactly six decisions");
  }
  const answers = history.map(({ answer }) => answer);
  const route = story.routes.find((candidate) => routeMatches(candidate, answers));
  if (!route) {
    throw new Error(`Missing route for ${answers.join("/")}`);
  }
  return route;
}

function variantMatches(
  history: Pick<HistoryEntry, "sceneId" | "answer">[],
  conditions: { sceneId: string; answer: Answer }[],
): boolean {
  return conditions.every((condition) =>
    history.some(
      (entry) => entry.sceneId === condition.sceneId && entry.answer === condition.answer,
    ),
  );
}

export function getScenePresentation(
  scene: Scene,
  history: Pick<HistoryEntry, "sceneId" | "answer">[],
): Pick<Scene, "artId" | "situation"> {
  const matches = (scene.variants ?? []).filter((variant) =>
    variantMatches(history, variant.when.historyIncludes),
  );
  if (matches.length > 1) {
    throw new Error(`Multiple presentation variants match scene ${scene.id}`);
  }
  const variant = matches[0];
  return {
    artId: variant?.artId ?? scene.artId,
    situation: variant?.situation ?? scene.situation,
  };
}
